import React, { useCallback, useEffect, useState, useContext } from 'react';
import { SearchFilterContentTypes, SearchOption } from 'stockblocks-client';
import { twMerge } from 'tailwind-merge';

import { StatsigExperimentContext } from '../context/NavSearchContext';
import { NavSearchContext, NavSearchContextProvider, NavSearchProps } from '../context/NavSearchContext';
import { CrossAssetNavSuggestions } from '../types/shared';
import { SearchOrigins } from '../utils/constants';
import { useMedia } from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import { cleanSearchKeywords, SearchBarVariant } from './NavSearchElements/searchUtils';
import ProminentSearchButton from './ProminentSearchElements/ProminentSearchButton';
import ProminentSearchDropdown from './ProminentSearchElements/ProminentSearchDropdown';

export interface ProminentSearchProps extends NavSearchProps {
    onDropdownChange: (contentType: string) => void;
    searchTerm: string;
    prominentSearchOptions: SearchOption[];
    contentTypeFromPrevious: SearchFilterContentTypes;
    showDropDown: boolean;
    showContentToggles?: boolean;
    searchGatewayFetch?: boolean;
}

export default function ProminentSearch(props: ProminentSearchProps) {
    const {
        onDropdownChange,
        onSubmit,
        userHasAnySubscription,
        prominentSearchOptions,
        contentTypeFromPrevious,
        showDropDown,
        showContentToggles = false,
        useSnapshots = false,
        searchGatewayFetch = false,
    } = props;

    const defaultSearchOption = prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

    const [contentType, setContentType] = useState(defaultSearchOption ?? contentTypeFromPrevious);
    const [searchTerm, setSearchTerm] = useState(props.searchTerm ?? '');
    const context = useContext(StatsigExperimentContext);

    // Ensure searchTerm stays up to date when given a new one via props.
    useEffect(() => {
        setSearchTerm(props.searchTerm ?? '');
    }, [props.searchTerm]);

    useEffect(() => {
        setContentType(contentTypeFromPrevious);
    }, [contentTypeFromPrevious]);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);

    const handleDropdownChange = (contentType: SearchFilterContentTypes) => {
        onDropdownChange(contentType);
        setContentType(contentType);
    };

    const makeEmptySuggestions = (): Partial<CrossAssetNavSuggestions> => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);
    const handleSubmit = (
        contentTypeSearched: SearchFilterContentTypes = contentType,
        value: string = searchTerm,
        searchOriginOverride: string = '',
    ) => {
        const cleanedSearchKeywords = cleanSearchKeywords(value);
        const searchOrigin = searchOriginOverride || SearchOrigins.SEARCH_BAR;

        if (context?.isSearchHistoryEnabled) {
            saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
        }
        onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
    };

    const activeTab = 'bg-white text-gray-900 thick-3 offset-5';
    const inactiveTab = 'text-white bg-gray-900/15';

    return (
        <NavSearchContextProvider {...props}>
            <NavSearchContext.Consumer>
                {({ setShowSuggestions, searchBarVariant }) => (
                    <div
                        className={twMerge(
                            'flex flex-col w-full font-inter text-gray-900 items-center ',
                            showContentToggles ? 'pb-8' : '',
                            searchBarVariant === SearchBarVariant.TopNavSearch
                                ? 'rounded-full border border-solid border-gray-400'
                                : '',
                        )}
                    >
                        {showContentToggles && (
                            <div className="product m-0 flex text-base font-bold grid-rows-4 pb-4 md:pb-6 lg:pb-6 cursor-pointer gap-3">
                                {/* Contents of the product div */}
                                {prominentSearchOptions.map((link) => (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        key={link.id}
                                        className={twMerge(
                                            'text-base rounded-sm px-2 py-1 border-0',
                                            contentType === link.id ? activeTab : inactiveTab,
                                        )}
                                        data-value={link.id}
                                        onClick={() => handleDropdownChange(link.id)}
                                        onKeyDown={() => handleDropdownChange(link.id)}
                                    >
                                        {link.label}
                                    </div>
                                ))}
                            </div>
                        )}
                        <form
                            className={twMerge(
                                'border-none rounded-full lg:w-[822px] md:w-[480px] flex items-center bg-white',
                                searchBarVariant === SearchBarVariant.TopNavSearch ? '' : 'pl-5',
                            )}
                            onSubmit={(event) => {
                                event.preventDefault();
                                setShowSuggestions(false);
                                handleSubmit(contentType, searchTerm);
                            }}
                        >
                            {showDropDown && (
                                <>
                                    <ProminentSearchDropdown
                                        onChange={handleDropdownChange}
                                        value={contentType}
                                        useSnapshots={useSnapshots}
                                        clearSuggestions={clearSuggestions}
                                        useNewSearchUI={searchBarVariant === SearchBarVariant.TopNavSearch}
                                    />
                                    {searchBarVariant !== SearchBarVariant.TopNavSearch && (
                                        <div>
                                            <div className="w-px h-11 bg-gray-500" />
                                        </div>
                                    )}
                                </>
                            )}
                            {isMediumScreen ? (
                                <CrossAssetNavSearchBox
                                    contentType={contentType}
                                    setSearchTerm={setSearchTerm}
                                    onSubmit={handleSubmit}
                                    searchTerm={searchTerm}
                                    suggestions={suggestions}
                                    setSuggestions={setSuggestions}
                                    clearSuggestions={clearSuggestions}
                                    placeholder={
                                        prominentSearchOptions.find(({ id }) => id === contentType)?.placeholderText
                                    }
                                    searchGatewayFetch={searchGatewayFetch}
                                />
                            ) : (
                                <NavSearchBox
                                    contentType={contentType}
                                    onChange={setSearchTerm}
                                    onSubmit={handleSubmit}
                                    searchTerm={searchTerm}
                                    placeholder={
                                        prominentSearchOptions.find(({ id }) => id === contentType)?.placeholderText
                                    }
                                    userHasAnySubscription={userHasAnySubscription}
                                    isProminentSearch
                                />
                            )}
                            <ProminentSearchButton />
                        </form>
                    </div>
                )}
            </NavSearchContext.Consumer>
        </NavSearchContextProvider>
    );
}
